import Utils from './utils';
import gridGuide from 'cytoscape-grid-guide';
import cytoscape from 'cytoscape';
import Panel from '../shared/panels';
import { defaultTheme } from '../../../../src/themes';

/*
	Graphy is a shared superclass between the Editor and the Viewer which implements
	generic and shared graph behavior, such as bloom animations, locking, zoom management, 
	edge traversal logic and similar.

	It should generally not concern itself with content or similar. 
*/
export default class Graphy {
  constructor(options) {
    this.options = options;
    this.slotSortOrder = [6, 7, 0, 1, 2, 3, 4, 5];
    this.userUuid = Utils.getUserId();
    this.startNodeIcon = null;
    this.startHub = null;
  }

  initializeGrid() {
    if (!window.editor) {
      return;
    }

    gridGuide(cytoscape);

    this.graph.cy.gridGuide({
      drawGrid: Utils.getGridSetting('showGrid', false),
      snapToGridDuringDrag: Utils.getGridSetting('snapToGrid', false),
      gridSpacing: Utils.getGridSetting('gridSize', 30),
      panGrid: true,
      snapToGridCenter: false,
      snapToGridOnRelease: false,
      gridStackOrder: 0,
      gridColor: defaultTheme.colors.white_3,
    });

    $(this.graph.cy.container()).children('div').css('z-index', 1);
  }

  getBrandingProfile() {
    const branding = (this.options.organization || {}).branding;
    return branding && branding.profile;
  }

  updateEdgeColors(brandingProfile) {
    const profile = brandingProfile || this.getBrandingProfile();
    if (profile && profile.loopPathColor) {
      this.graph.cy.edges().style({ 'line-color': profile.loopPathColor });
    }
  }

  setReadability(enable) {
    setTimeout(() => {
      const elements = this.graph.cy.elements();
      if (enable) {
        elements.addClass('readability');
      } else {
        elements.removeClass('readability');
      }
      window.requestAnimationFrame(() => {
        this.updateStartHubIconNode();
      });
    }, 250);
  }

  build(parent) {
    this.graph.cy.$('node').renderedBoundingBox();

    this.initializeGrid();

    const profile = this.getBrandingProfile();
    if (profile && profile.loopBackgroundColor && profile.loopBackgroundColor !== 'transparent') {
      $('#viewer, #editor').css('background', profile.loopBackgroundColor);
      this.updateEdgeColors(profile);
    } else {
      this.useCanvallax = true;
    }

    if (this.useCanvallax) {
      this.canvallax = Canvallax({
        parent: $(parent).get(0),
        damping: 2,
        fullscreen: true,
        tracking: 'manual',
      });

      const starColors = [
        '#9db4ff',
        '#a2b9ff',
        '#a7bcff',
        '#aabfff',
        '#afc3ff',
        '#baccff',
        '#c0d1ff',
        '#cad8ff',
        '#e4e8ff',
        '#edeeff',
        '#fbf8ff',
        '#fff9f9',
        '#fff5ec',
        '#fff4e8',
        '#fff1df',
        '#ffebd1',
        '#ffd7ae',
        '#ffc690',
        '#ffbe7f',
        '#ffbb7b',
        '#ffbb7b',
      ];

      for (var i = 0; i < 1500; i++) {
        let el = Canvallax.Circle({
          size: 2,
          x: Math.floor(Math.random() * (this.canvallax.width + 2000 - -1000 + 1)) + -1000,
          y: Math.floor(Math.random() * (this.canvallax.height + 2000 - -1000 + 1)) + -1000,
          fill: starColors[Math.floor(Math.random() * starColors.length)],
          distance: Math.random(),
        });
        this.canvallax.add(el);
      }

      this.canvallax.x = 1;
      this.canvallax.play();
    }

    this.graph.cy.on('pan', (e) => {
      let _current = this.graph.cy.pan();
      if (!this.currentPan) {
        this.currentPan = { x: _current.x, y: _current.y };
      }

      if (this.useCanvallax) {
        this.canvallax.x = (this.currentPan.x - _current.x) * 0.08;
        this.canvallax.y = (this.currentPan.y - _current.y) * 0.08;
      }
    });

    this.graph.cy.on(
      'pan zoom layoutstart',
      (this.manipulationHandler = (e) => {
        $(document).trigger('move.graph');
      }),
    );

    $('.lp--js-graphZoomIn').on(Utils.interactionEvent, (ev) => {
      this.changeZoom(1);
    });

    $('.lp--js-graphZoomOut').on(Utils.interactionEvent, (ev) => {
      this.changeZoom(-1);
    });

    $('.lp--js-graphCenter').on(Utils.interactionEvent, (ev) => {
      this.centerGraph(true);
    });

    $('.remote-control .up').on(Utils.interactionEvent, (ev) => {
      this.changeZoom(1);
    });

    $('.remote-control .down').on(Utils.interactionEvent, (ev) => {
      this.changeZoom(-1);
    });

    $('.remote-control .center').on(Utils.interactionEvent, (ev) => {
      this.centerGraph(true);
    });

    $('.remote-control .left').on(Utils.interactionEvent, (ev) => {
      this.showWhereIAm();
    });

    $('.remote-control .right').on(Utils.interactionEvent, (ev) => {
      this.showWholeGraph();
    });

    $(document).on('dyslexicFontChange', (event, enable) => {
      this.setReadability(enable);
    });

    const readabilitySetup = window.localStorage.getItem('readability');
    if (readabilitySetup === '1') {
      this.setReadability(true);
    }
  }

  useZoomDependentStyles() {
    this.zoomHandler = Utils.debounce((e) => {
      this.updateZoomDependentStyles();
    }, 25);

    this.graph.cy.on('zoom', this.zoomHandler);
  }

  updateZoomDependentStyles() {
    const zoom = this.graph.cy.zoom();
    const classesToRemove = 'zoom_1 zoom_2 zoom_3 zoom_4 zoom_5 zoom_6 zoom_7';

    let classToAdd = '';
    if (zoom > 1.3) {
      classToAdd = 'zoom_1';
    } else if (zoom > 1.1 && zoom <= 1.3) {
      classToAdd = 'zoom_2';
    } else if (zoom > 0.9 && zoom <= 1.1) {
      classToAdd = 'zoom_3';
    } else if (zoom > 0.7 && zoom <= 0.9) {
      classToAdd = 'zoom_4';
    } else if (zoom > 0.5 && zoom <= 0.7) {
      classToAdd = 'zoom_5';
    } else if (zoom > 0.3 && zoom <= 0.5) {
      classToAdd = 'zoom_6';
    } else if (zoom <= 0.3) {
      classToAdd = 'zoom_7';
    }

    this.graph.cy.batch(() => {
      this.graph.cy.$('node').removeClass(classesToRemove).addClass(classToAdd);
      this.graph.cy.$('edge').removeClass(classesToRemove).addClass(classToAdd);
    });
    $('body').removeClass(classesToRemove).addClass(classToAdd);
    this.graph.cy.resize();

    if (this.isHubCreationAllowed && this.isHubCreationAllowed()) {
      this.graph.cy.$('.eh-handle').removeClass('disabled');
    } else {
      this.graph.cy.$('.eh-handle').addClass('disabled');
    }
  }

  stopGridSnap() {
    if (this.graph.cy.gridGuide) {
      this.graph.cy.gridGuide({ snapToGridDuringDrag: false });
    }
  }

  resetGridSnap() {
    if (this.graph.cy.gridGuide) {
      this.graph.cy.gridGuide({ snapToGridDuringDrag: Utils.getGridSetting('snapToGrid', false) });
    }
  }

  mouseOver(node) {
    if (!this.graph.cy.scratch('edge-handle.active')) {
      node.addClass('hover');
      $('canvas').addClass('pointer');
      node.hover = true;
      if (!node.data().group && !this.isEdgeHandle(node)) {
        this.graph.setHandleOn(node);
      } else if (this.isEdgeHandle(node)) {
        this.graph.handleHover = true;
      }
    }
  }

  mouseOut(node) {
    if (!node.hover) {
      node.removeClass('hover');
      $('canvas').removeClass('pointer');
      if (!node.data().group && !this.isEdgeHandle(node)) {
        this.graph.removeHandle();
      } else if (this.isEdgeHandle(node)) {
        this.graph.handleHover = false;
      }
    }
    this.resetGridSnap();
  }

  mouseDragStart(node) {
    if (this.options.allowNodeMovement) {
      node.dragging = true;
      $('canvas').removeClass('pointer');
      $('canvas').addClass('grabbing');
      if (!this.isHub(node)) {
        this.stopGridSnap();
      }
    }
  }

  mouseDragEnd(node) {
    node.dragging = false;
    $('canvas').addClass('pointer');
    $('canvas').removeClass('grabbing');
    this.resetGridSnap();
  }

  isEdgeHandle(node) {
    return node.classes().includes('eh-handle');
  }

  isHub(node) {
    return !node.data().type;
  }

  getSlotPlacement(slot) {
    switch (slot) {
      case 1:
        return 'top';
      case 2:
      case 3:
      case 4:
        return 'right';
      case 5:
        return 'bottom';
      case 6:
      case 7:
      case 8:
        return 'left';
      default:
        return 'auto';
    }
  }

  updateHeaderPosition(node) {
    const order = this.slotSortOrder.indexOf(node.data().slot) + 1;
    const placement = this.getSlotPlacement(order);
    const nodeHeader = node.data().header;
    if (nodeHeader) {
      nodeHeader.state.options.placement = placement;
    }
  }

  updateStartHub(uuid) {
    const previousStartHub = this.graph.cy.$('.start-hub');
    if (previousStartHub) {
      previousStartHub.removeClass('start-hub');
      previousStartHub.removeListener('position');
    }

    this.startHub = this.graph.cy.$(`#${uuid}`);
    if (this.startHub) {
      this.startHub.addClass('start-hub');
      this.startHub.on('position', this.updateStartHubIconNode.bind(this));
      this.updateStartHubIconNode();
    }
  }

  getStartHubIconPosition(node) {
    const zoom = this.graph.cy.zoom();
    const { rstyle } = node._private;
    let marginX = 0,
      marginY = 0;
    /* wish this was possible to handle in styling, but haven't been successful at that
       -20 / -30 due to header y-adjustment when zooming at level 6 or 7 - node headers are hidden at that instance
      and we do want to keep the header close to the hub */
    if (zoom > 0.3 && zoom <= 0.5) {
      marginX = 12;
      marginY = 2 - 20;
    } else if (zoom <= 0.3) {
      marginX = 20;
      marginY = 4 - 30;
    }

    return {
      x: rstyle.labelX - rstyle.labelWidth / 2 - 24 - marginX,
      y: rstyle.labelY - 94 - rstyle.labelHeight / 2 - marginY,
    };
  }

  updateStartHubIconNode() {
    if (this.startNodeIcon && this.startHub) {
      setTimeout(() => {
        this.startNodeIcon[0].position(this.getStartHubIconPosition(this.startHub[0]));
      }, 10);
    }
  }

  setNodeHeaders() {
    this.graph.cy.ready(() => {
      this.startHub = this.graph.cy.$('.start-hub');
      const startHubId = this.startHub.id();

      this.startNodeIcon = this.graph.cy.add({
        group: 'nodes',
        data: { group: 'indicators', type: 'startHubIcon' },
        classes: 'start-hub-icon hidden',
      });
      this.graph.cy.on('zoom resize pan', this.updateStartHubIconNode.bind(this));

      this.graph.cy
        .nodes()
        .filter((node) => node.length)
        .forEach((node) => {
          const isStartHub = startHubId === node.id();

          const nodeData = node.neighborhood().data();
          if ($('#editor').length > 0 || !node.data().type || (nodeData && nodeData.lock_state === 'unlocked')) {
            if (isStartHub) {
              this.startNodeIcon.removeClass('hidden');
              this.updateStartHub(node.id());
            }
          }
        });
    });
    if (this.graph.cy.zoom <= 0.5) {
      $('.node-header').animate({ opacity: 1 }, 1000, 'swing');
    } else {
      $('.node-header.hub').animate({ opacity: 1 }, 1000, 'swing');
    }
  }

  getFirstUndoneNode() {
    const currentUuid = $('.activity-item:not(.done)').data('uuid');
    return currentUuid && this.graph.cy.$(`#${currentUuid}`);
  }

  centerGraph(useCurrent = false) {
    const currentNode = useCurrent ? this.currentNode : this.getFirstUndoneNode();
    if (currentNode) {
      let pan = this.determinePanPosition(currentNode);
      this.graph.cy.animate({
        zoom: 1.1,
        pan: pan,
      });
    } else {
      this.graph.cy.animate({ fit: { padding: 0 } });
    }
  }

  resizeGraphToViewport() {
    let width = $('.content-panel.circle-pane.open').width() || 0;
    $('#viewer').css({
      width: `calc(100% - ${width}px)`,
      left: `${width}px`,
    });
    this.graph.cy.resize();
    this.fitGraph();
    $('.pr2--js-closeBtn').one('click', () => {
      this.resetViewport();
      this.graph.cy.panBy({ x: width, y: 60 });
    });
  }

  resetViewport() {
    if ($('#viewer, #editor').position().left > 0) {
      $('#viewer, #editor').css({ width: '100%', left: '0' });
    }
  }

  fitGraph() {
    this.graph.cy.animate({ fit: { padding: 120 } });
  }

  showWholeGraph() {
    if (Panel.hasPanelDisplayed()) {
      this.resetViewport();
      Panel.currentPanel.hide().then(() => {
        this.fitGraph();
      });
    } else {
      this.fitGraph();
    }
  }

  showWhereIAm() {
    $(document).trigger('loopNameClicked', {
      hideDescription: true,
    });
  }

  changeZoom(direction, animated = true) {
    const maxZoom = this.graph.cy.maxZoom();
    const minZoom = this.graph.cy.minZoom();
    const zoom = this.graph.cy.zoom();
    const step = (maxZoom - minZoom) / 6;
    let newLevel = (zoom + direction * step).toFixed(2) * 1;

    if (newLevel > maxZoom) {
      newLevel = maxZoom;
    } else if (newLevel < minZoom) {
      newLevel = minZoom;
    }

    if (animated && newLevel !== zoom) {
      let bound = this.graph.cy.extent();
      let mid_x = (bound.x1 + bound.x2) / 2;
      let mid_y = (bound.y1 + bound.y2) / 2;
      this.graph.cy.animate({
        zoom: {
          level: newLevel,
          position: { x: mid_x, y: mid_y },
        },
      });
    }
  }

  determinePanPosition(node) {
    const windowWidth = $(window).width();
    const contentPanel = $('.content-panel').width() || 0;
    const space = windowWidth - contentPanel;

    const zoom = 1.1;
    const xDiff = this.graph.cy.width() / 2 - space / 2;
    const bb = node.boundingBox();
    const w = this.graph.cy.width();
    const h = this.graph.cy.height();
    const pan = {
      x: (w - zoom * (bb.x1 + bb.x2)) / 2 + xDiff,
      y: (h - zoom * (bb.y1 + bb.y2)) / 2,
    };
    return pan;
  }

  resolveAccessModel() {
    if (!this.options.start_node) {
      return;
    }
    let elements = this.graph.cy.elements();
    let bf = elements.bellmanFord({
      root: '#' + this.options.start_node,
      directed: false,
      weight: function (edge) {
        if (edge.target().data('lock_state') != 'unlocked' || edge.source().data('lock_state') != 'unlocked') {
          return Number.MAX_SAFE_INTEGER;
        } else {
          return 1;
        }
      },
    });
    let unLockedSet = this.graph.cy.collection();
    elements
      .filter('node')
      .difference('#' + this.options.start_node)
      .forEach((element, i, eles) => {
        if (bf.distanceTo(element) < Number.MAX_SAFE_INTEGER) {
          unLockedSet = unLockedSet.union(bf.pathTo(element));
        }
      });
    unLockedSet.removeData('metaLockedBy').removeClass('codeMetaLocked lockedMetaLocked');

    let meta_locks = unLockedSet.absoluteComplement().filter('node').difference("[lock_state != 'unlocked']");
    meta_locks.forEach((element, i, eles) => {
      let path = bf.pathTo(element);
      for (var i = path.length - 2; i >= 0; i--) {
        if (path[i].is('node')) {
          if (path[i].data('lock_state') != 'unlocked') {
            element
              .data('metaLockedBy', path[i].data('lock_state'))
              .addClass(path[i].data('lock_state') + 'MetaLocked');
          }
        }
      }
    });
    this.graph.cy
      .nodes('[?type][!metaLockedBy][group != "indicators"]')
      .filter((node) => {
        if (node && node.neighbourhood('[!group]').data()) {
          return node.neighbourhood('[!group]').data().lock_state === 'locked';
        } else {
          return false;
        }
      })
      .forEach((node) => {
        if (node && node.data()) {
          node.data().lock_state = 'locked';
        }
      });
  }

  checkHubCompletion(node) {
    let hubs = null;
    if (!node) {
      hubs = this.graph.cy.$('node[!group]');
    } else if (node.is('node[!group]')) {
      hubs = node;
    } else {
      hubs = node.neighborhood('node[!group]');
    }
    if (!hubs) {
      return;
    }
    for (var i = 0; i < hubs.length; i++) {
      if (hubs[i].is('node[!group]')) {
        let activities = hubs[i].neighborhood('node[?group]');
        if (activities.allAre('.checkmark')) {
          if (!hubs[i].hasClass('hub_completed')) {
            hubs[i].addClass('hub_completed');
          }
        } else {
          if (hubs[i].hasClass('hub_completed')) {
            hubs[i].removeClass('hub_completed');
          }
        }
      }
    }
  }
}
